import { Stack, Typography } from '@mui/material';
import { Fragment, useState } from 'react';
import { Trans } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';

import { Divider } from '@/components/Layout/Divider';

import { useDrawerOpenTracker } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectOperationData } from '@/redux/operation/selectors';
import { selectUserData } from '@/redux/user-data';

import {
  Document,
  DocumentSection,
  DocumentStatus,
  RequiredDocument,
} from '@/types/document';

import { isUnconfirmedAdditionalIdDocument } from '@/utils/document';

import { RequiredDocumentGroup } from '../../RequiredDocuments/RequiredDocumentGroup';
import { RequiredDocumentUploadDrawer } from '../RequiredDocumentUploadDrawer';
import SecondIdConfirmDrawer from '../SecondIdConfirmDrawer';
import { ViewDocumentDrawer } from '../ViewDocumentDrawer';

type SecondIDSectionProps = {
  secondIdGuidance: {
    photoIdsList: string;
    nonPhotoIdsList: string;
  } | null;
  secondIdCopy?: {
    headerKey: string;
    guidanceKey: string;
  };
  clientId?: string | number;
  onConfirmed?: () => void;
};
export const SecondIDSection = ({
  secondIdGuidance,
  secondIdCopy,
  clientId,
  onConfirmed,
}: SecondIDSectionProps) => {
  const [docToUpload, setDocToUpload] = useState<RequiredDocument>();
  const [docToView, setDocToView] = useState<Document>();
  const {
    value: isOpenUploadDrawer,
    setTrue: openUploadDrawer,
    setFalse: closeUploadDrawer,
  } = useBoolean(false);
  const {
    value: isOpenViewDrawer,
    setTrue: openViewDrawer,
    setFalse: closeViewDrawer,
  } = useBoolean(false);
  const {
    value: isOpenSecondIdConfirmDrawer,
    setTrue: openSecondIdConfirmDrawer,
    setFalse: closeSecondIdConfirmDrawer,
  } = useBoolean(false);

  useDrawerOpenTracker(
    isOpenUploadDrawer || isOpenViewDrawer || isOpenSecondIdConfirmDrawer,
  );

  const { t, translateIfExists } = useTranslate();
  const userData = useAppSelector(selectUserData);
  const operationData = useAppSelector(selectOperationData);
  const { requiredDocumentSections, documentByDocType } = clientId
    ? operationData
    : userData;
  const secondIDSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.SecondID,
  );

  const showSecondIdHeader = !!(secondIdGuidance && secondIdCopy);

  if (!secondIDSection) return null;

  const onSelectDocTypeForSecondID = (doc: RequiredDocument) => {
    const docKey = doc.groupedAs || doc.name;
    const targetSectionType = String(secondIDSection.type);
    const docToView = documentByDocType[targetSectionType]?.[docKey];
    setDocToUpload(doc);
    setDocToView(docToView);
    if (isUnconfirmedAdditionalIdDocument(docToView)) {
      openSecondIdConfirmDrawer();
      return;
    }

    if (doc.status === DocumentStatus.Pending) {
      openUploadDrawer();
    } else {
      openViewDrawer();
    }
  };

  const onDocumentUploaded = () => {
    closeUploadDrawer();
    closeViewDrawer();
    openSecondIdConfirmDrawer();
  };

  const onSecondIdConfirmed = () => {
    closeSecondIdConfirmDrawer();
    onConfirmed?.();
  };

  return (
    <>
      {showSecondIdHeader && (
        <Stack direction="column" gap={2} alignItems="center">
          <Typography variant="h3" textAlign="center">
            {t(secondIdCopy.headerKey)}
          </Typography>
          <Typography variant="body2" textAlign="center">
            <Trans
              i18nKey={secondIdCopy.guidanceKey}
              values={{
                photoIdTypes: secondIdGuidance.photoIdsList,
                nonPhotoIdTypes: secondIdGuidance.nonPhotoIdsList,
              }}
            />
          </Typography>
        </Stack>
      )}
      {(secondIDSection.groups || []).map((group, index) => (
        <Fragment key={index}>
          <RequiredDocumentGroup
            group={group}
            onItemSelect={onSelectDocTypeForSecondID}
            showHeader={false}
          />
          {index <
            (!secondIDSection.groups
              ? 0
              : secondIDSection.groups.length - 1) && (
            <Divider
              width="100%"
              containerProps={{
                my: 4,
              }}
            />
          )}
        </Fragment>
      ))}
      <RequiredDocumentUploadDrawer
        open={isOpenUploadDrawer}
        section={DocumentSection.SecondID}
        type={docToUpload?.groupedAs || docToUpload?.name}
        clientId={clientId}
        onClose={closeUploadDrawer}
        onUploaded={onDocumentUploaded}
        title={translateIfExists(`documentType.${docToUpload?.name}`, {
          defaultValue: t('documentSection.Second_ID.title'),
        })}
      />
      <ViewDocumentDrawer
        open={isOpenViewDrawer}
        section={secondIDSection}
        document={docToView}
        clientId={clientId}
        onClose={closeViewDrawer}
        title={t('documentSection.Second_ID.title')}
        onReUploaded={onDocumentUploaded}
      />
      <SecondIdConfirmDrawer
        open={isOpenSecondIdConfirmDrawer}
        section={secondIDSection}
        idType={docToUpload?.name}
        clientId={clientId}
        uploadedDocumentId={
          documentByDocType[String(secondIDSection.type)]?.[
            docToUpload?.name || ''
          ]?.id
        }
        onClose={closeSecondIdConfirmDrawer}
        onConfirmed={onSecondIdConfirmed}
        title={translateIfExists(`documentType.${docToView?.docType}`, {
          defaultValue: t('documentSection.Second_ID.title'),
        })}
      />
    </>
  );
};
